import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState
} from 'react';
import { supabase } from '../services/supabaseClient';

const AuthContext = createContext(null);

const VALID_ROLES = ['guest', 'client', 'cro', 'docs', 'visa', 'agreements', 'education', 'manager', 'admin'];
const STAFF_ROLES = new Set(['admin', 'manager', 'agreements', 'docs', 'visa', 'cro', 'education']);

const extractProfileFromRpc = (payload) => {
  if (!payload) return null;
  if (Array.isArray(payload)) {
    const [first] = payload;
    return first ? extractProfileFromRpc(first) : null;
  }

  if (typeof payload === 'object') {
    if (payload === null) {
      return null;
    }

    if ('profile' in payload) {
      return extractProfileFromRpc(payload.profile);
    }

    return payload;
  }

  return null;
};

const isRpcFailurePayload = (payload) => {
  return (
    payload &&
    typeof payload === 'object' &&
    !Array.isArray(payload) &&
    Object.prototype.hasOwnProperty.call(payload, 'success') &&
    payload.success === false
  );
};

const deriveRole = (currentUser, staffProfile, hasAuthError = false) => {
  // Check metadata role first
  const metadataRole =
    currentUser?.app_metadata?.role ||
    currentUser?.user_metadata?.role;

  // If authentication succeeded but RPC failed for a staff user, use metadata role
  // This allows staff users to continue even if RPC is temporarily unavailable
  if (currentUser && hasAuthError) {
    if (metadataRole && VALID_ROLES.includes(metadataRole)) {
      return metadataRole;
    }
  }

  // For staff users, always use the metadata role (never use role overrides)
  if (metadataRole && STAFF_ROLES.has(metadataRole)) {
    return metadataRole;
  }

  return (
    staffProfile?.role_key ||
    metadataRole ||
    'guest'
  );
};

// Cache busting - force browser to recognize AuthContext changes
const AUTH_CONTEXT_VERSION = '20251115_120000';

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [profile, setProfile] = useState(null);
  const [role, setRole] = useState('guest'); // guest, cro, docs, visa, agreements, manager, admin
  const [loading, setLoading] = useState(true);

  // Check cache version on init — preserve Supabase auth keys when clearing
  useEffect(() => {
    try {
      const currentVersion = localStorage.getItem('auth_context_version');
      if (currentVersion !== AUTH_CONTEXT_VERSION) {
        console.log('[Auth] Cache version changed. Clearing app keys but preserving Supabase auth session.');
        const keysToRemove = [];
        // Supabase auth keys start with 'sb-' (e.g., sb-<project-ref>-auth-token)
        const preservePrefixes = ['sb-'];
        for (let i = 0; i < localStorage.length; i += 1) {
          const k = localStorage.key(i);
          if (!k) continue;
          const shouldPreserve = preservePrefixes.some((p) => k.startsWith(p));
          if (!shouldPreserve && k !== 'auth_context_version') {
            keysToRemove.push(k);
          }
        }
        keysToRemove.forEach((k) => localStorage.removeItem(k));
        // Avoid clearing entire sessionStorage as some environments may store auth there
        // If you must clear, do so selectively similar to localStorage above.
        localStorage.setItem('auth_context_version', AUTH_CONTEXT_VERSION);
      }
    } catch {
      // ignore storage errors (private mode etc.)
    }
  }, []);

  const ROLE_OVERRIDE_KEY = 'rm_role_override';

  const getOverride = useCallback(() => {
    if (typeof window === 'undefined') return null;
    try {
      const stored = window.localStorage.getItem(ROLE_OVERRIDE_KEY);
      if (!stored || !VALID_ROLES.includes(stored)) {
        if (stored) window.localStorage.removeItem(ROLE_OVERRIDE_KEY);
        return null;
      }
      if (stored === 'guest') {
        window.localStorage.removeItem(ROLE_OVERRIDE_KEY);
        return null;
      }
      return stored;
    } catch {
      return null;
    }
  }, []);

  const ensureProfile = useCallback(
    async (sessionUser) => {
      if (!sessionUser) {
        setProfile(null);
        return { profile: null, error: null };
      }

      const roleKey =
        sessionUser.user_metadata?.role ||
        sessionUser.app_metadata?.role ||
        null;

      const hasMetadataRole = !!roleKey;
      const isStaffRole = hasMetadataRole && STAFF_ROLES.has(roleKey);

      // If metadata does not declare a staff role, check if this user already has a staff profile.
      // This supports staff who were onboarded via the internal admin tools without updating auth metadata.
      if (!isStaffRole) {
        try {
          const { data, error } = await supabase.rpc('admin_get_staff_profile', {
            p_auth_user_id: sessionUser.id
          });

          if (error) {
            console.warn('[Auth] admin_get_staff_profile failed while probing for staff profile', error);
            setProfile(null);
            return { profile: null, error };
          }

          if (isRpcFailurePayload(data)) {
            const failureMessage =
              data?.error ||
              data?.message ||
              'admin_get_staff_profile returned an unsuccessful response';
            console.warn('[Auth] Staff profile RPC returned failure payload when probing', failureMessage);
            setProfile(null);
            return { profile: null, error: new Error(failureMessage) };
          }

          const existingProfile = extractProfileFromRpc(data);
          if (existingProfile && existingProfile.role_key && STAFF_ROLES.has(existingProfile.role_key)) {
            // Treat as staff based on profile.role_key even if auth metadata lacks role.
            setProfile(existingProfile);
            return { profile: existingProfile, error: null };
          }

          // No staff profile found – treat as non-staff (client/guest).
          console.info('[Auth] No staff profile found; treating user as non-staff');
          setProfile(null);
          return { profile: null, error: null };
        } catch (err) {
          console.warn('[Auth] admin_get_staff_profile not available when probing for staff profile', err);
          setProfile(null);
          return { profile: null, error: err };
        }
      }

      // For users whose metadata explicitly declares a staff role, ensure their staff profile exists.
      try {
        const { data, error } = await supabase.rpc('admin_ensure_staff_profile', {
          p_auth_user_id: sessionUser.id,
          p_email: sessionUser.email,
          p_name:
            sessionUser.user_metadata?.full_name ||
            sessionUser.user_metadata?.name ||
            sessionUser.email,
          p_role: roleKey
        });

        if (error) {
          console.warn('[Auth] Failed to ensure staff profile (this is expected if RPC does not exist yet)', error);
          // Return error flag to allow fallback logic to handle this
          setProfile(null);
          return { profile: null, error };
        }

        if (isRpcFailurePayload(data)) {
          const failureMessage =
            data?.error ||
            data?.message ||
            'admin_ensure_staff_profile returned an unsuccessful response';
          const failure = new Error(failureMessage);
          failure.code = data?.error_code || 'PROFILE_OPERATION_FAILED';
          console.warn('[Auth] Staff profile RPC returned failure payload', failureMessage);
          setProfile(null);
          return { profile: null, error: failure };
        }

        const seededProfile = extractProfileFromRpc(data);
        setProfile(seededProfile || null);
        return { profile: seededProfile || null, error: null };
      } catch (err) {
        console.warn('[Auth] RPC admin_ensure_staff_profile not available - using fallback', err);
        // Return error flag to allow fallback logic to handle this
        setProfile(null);
        return { profile: null, error: err };
      }
    },
    []
  );

  const applyRoleFromUser = useCallback(
    (currentUser, overrideOverride, nextProfile, hasAuthError = false) => {
      // Check if user is a staff member from metadata
      const metadataRole =
        currentUser?.app_metadata?.role ||
        currentUser?.user_metadata?.role;
      const isStaffUser = metadataRole && STAFF_ROLES.has(metadataRole);

      // For staff users, always ignore role overrides and use actual role
      if (isStaffUser) {
        // Explicitly clear any role override from localStorage
        try {
          if (typeof window !== 'undefined') {
            window.localStorage.removeItem(ROLE_OVERRIDE_KEY);
          }
        } catch {}
        const resolvedProfile = nextProfile ?? profile;
        const correctRole = deriveRole(currentUser, resolvedProfile, hasAuthError);
        setRole(correctRole);
        return;
      }

      // For non-staff users, allow role overrides
      const override =
        overrideOverride !== undefined ? overrideOverride : getOverride();
      if (override) {
        setRole(override);
        return;
      }
      const resolvedProfile = nextProfile ?? profile;
      setRole(deriveRole(currentUser, resolvedProfile, hasAuthError));
    },
    [getOverride, profile]
  );

  const syncSession = useCallback(
    async (sessionUser) => {
      setUser(sessionUser);
      const { profile: staffProfile, error } = await ensureProfile(sessionUser);
      // Pass error flag to allow fallback role assignment
      const hasAuthError = !!error;
      applyRoleFromUser(sessionUser, undefined, staffProfile, hasAuthError);
    },
    [applyRoleFromUser, ensureProfile]
  );

  useEffect(() => {
    let isMounted = true;

    const init = async () => {
      setLoading(true);
      try {
        const {
          data: { session },
        } = await supabase.auth.getSession();
        if (!isMounted) return;
        const currentUser = session?.user || null;
        await syncSession(currentUser);
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    init();

    const { data: listener } = supabase.auth.onAuthStateChange((_event, session) => {
      if (!isMounted) return;
      const currentUser = session?.user || null;
      syncSession(currentUser);
      setLoading(false);
    });

    return () => {
      isMounted = false;
      listener?.subscription?.unsubscribe();
    };
  }, []); // Remove syncSession from deps to prevent infinite loop

  const setRoleOverride = useCallback(
    (nextRole) => {
      try {
        if (typeof window !== 'undefined') {
          if (nextRole && VALID_ROLES.includes(nextRole) && nextRole !== 'guest') {
            window.localStorage.setItem(ROLE_OVERRIDE_KEY, nextRole);
          } else {
            window.localStorage.removeItem(ROLE_OVERRIDE_KEY);
          }
        }
      } catch {}
      if (nextRole && VALID_ROLES.includes(nextRole) && nextRole !== 'guest') {
        setRole(nextRole);
      } else {
        setRole(deriveRole(user, profile));
      }
    },
    [user, profile]
  );

  const clearRoleOverride = useCallback(
    (currentUser, nextProfile) => {
      try {
        if (typeof window !== 'undefined') {
          window.localStorage.removeItem(ROLE_OVERRIDE_KEY);
        }
      } catch {}
      setRole(deriveRole(currentUser ?? user, nextProfile ?? profile));
    },
    [profile, user]
  );

  const signIn = useCallback(
    async ({ email, password }) => {
      setLoading(true);
      const { data, error } = await supabase.auth.signInWithPassword({ email, password });
      if (error) {
        setLoading(false);
        throw error;
      }
      const currentUser = data?.user || null;
      setUser(currentUser);
      const { profile: staffProfile, error: profileError } = await ensureProfile(currentUser);
      clearRoleOverride(currentUser, staffProfile);
      // Pass error flag to allow fallback role assignment
      applyRoleFromUser(currentUser, null, staffProfile, !!profileError);
      setLoading(false);
      return data;
    },
    [applyRoleFromUser, clearRoleOverride, ensureProfile]
  );

  const signOut = useCallback(async () => {
    setLoading(true);
    const { error } = await supabase.auth.signOut();
    if (error) {
      setLoading(false);
      throw error;
    }
    setUser(null);
    setProfile(null);
    clearRoleOverride(null, null);
    applyRoleFromUser(null, undefined, null);
    setLoading(false);
  }, [applyRoleFromUser, clearRoleOverride]);

  const refreshProfile = useCallback(async () => {
    if (!user?.id) return null;

    // Only refresh staff profiles, not for clients
    const roleKey = user?.user_metadata?.role || user?.app_metadata?.role || null;
    const isStaffRole = roleKey && STAFF_ROLES.has(roleKey);
    if (!isStaffRole) {
      console.info(`[Auth] Role '${roleKey}' is not a staff role, skipping profile refresh`);
      return null;
    }

    try {
      const { data, error } = await supabase.rpc('admin_get_staff_profile', {
        p_auth_user_id: user.id
      });
      if (error) {
        console.warn('[Auth] Failed to refresh staff profile', error);
        // Don't throw, just return null to use fallback
        return null;
      }
      if (isRpcFailurePayload(data)) {
        const failureMessage =
          data?.error ||
          data?.message ||
          'admin_get_staff_profile returned an unsuccessful response';
        console.warn('[Auth] Staff profile RPC returned failure payload', failureMessage);
        setProfile(null);
        return null;
      }
      const staffProfile = extractProfileFromRpc(data);
      setProfile(staffProfile || null);
      applyRoleFromUser(user, undefined, staffProfile);
      return staffProfile;
    } catch (err) {
      console.error('[Auth] Unexpected error refreshing profile', err);
      return null;
    }
  }, [applyRoleFromUser, user]);

  return (
    <AuthContext.Provider
      value={{
        user,
        profile,
        role,
        loading,
        signIn,
        signOut,
        setRoleOverride,
        clearRoleOverride,
        refreshProfile
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
};
